/** Parent card for the management {@link DataTable}. */
import * as React from 'react';
import { User, Users } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getParentBusinessStatus,
  PARENT_PENDING_STATUS_CONFIG,
  PARENT_ACTIVE_STATUS_CONFIG,
  PARENT_REMOTE_MILESTONES,
  getParentMilestoneStatus,
  type ParentTabView,
} from './parent-configs';
import type { ParentRecord } from '@/types/parent.types';
import {
  canShowArchiveRow,
  canShowReactivateRow,
  type ParentRowPageActions,
  deriveParentContact,
} from './parent-table-utils';

/** Props for {@link ParentCard}. */
interface ParentCardProps {
  record: ParentRecord;
  childCount?: number;
  childList?: Array<{ id: string; firstName: string; lastName: string }>;
  onView?: (id: string) => void;
  onEdit?: (id: string, record: ParentRecord) => void;
  onInviteChild?: (id: string, record: ParentRecord) => void;
  onViewStudents?: (id: string, record: ParentRecord) => void;
  pageActions?: ParentRowPageActions;
  activeTab: ParentTabView;
  onArchive?: (userId: string) => void;
  onReactivate?: (userId: string) => void;
}

/** Parent card for the management {@link DataTable}. */
export const ParentCard: React.FC<ParentCardProps> = ({
  record,
  childCount = 0,
  childList = [],
  onView,
  onEdit,
  onInviteChild,
  onViewStudents,
  pageActions,
  activeTab,
  onArchive,
  onReactivate,
}) => {
  const { tab, status } = getParentBusinessStatus({
    onboardingStatus: record.onboardingStatus,
    folderSituation: record.folderSituation,
    user: record.user,
    _childrenCount: childCount,
  });
  const userStatus = record.user?.status;
  const showArchive = canShowArchiveRow(userStatus, pageActions?.canArchiveRow);
  const showReactivate = canShowReactivateRow(userStatus, activeTab, pageActions?.canReactivateRow);

  function handleArchive(event: React.MouseEvent): void {
    event.stopPropagation();
    onArchive?.(record.userId);
  }

  function handleReactivate(event: React.MouseEvent): void {
    event.stopPropagation();
    onReactivate?.(record.userId);
  }

  const statusConfig =
    tab === 'pre_registered'
      ? PARENT_PENDING_STATUS_CONFIG[status as keyof typeof PARENT_PENDING_STATUS_CONFIG]
      : PARENT_ACTIVE_STATUS_CONFIG[status as keyof typeof PARENT_ACTIVE_STATUS_CONFIG];

  const StatusIcon = statusConfig?.icon;
  const sections = record.sections ?? {};
  const basicInfo = sections['basic_information'] as Record<string, unknown> | undefined;
  const firstName =
    record.user?.firstName ?? (basicInfo?.['firstName'] as string | undefined) ?? '—';
  const lastName = record.user?.lastName ?? (basicInfo?.['lastName'] as string | undefined) ?? '—';
  const primaryContact = deriveParentContact(
    sections,
    record.user?.phone,
    record.user?.email,
    record.user?.primaryContactKind,
  );

  const handleClick = () => {
    onView?.(record.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onView?.(record.id);
    }
  };

  return (
    <TableCard onClick={handleClick} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
      <div className="data-card-content">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[color-mix(in_srgb,var(--color-1-nuance-1)_12%,transparent)]">
            <User className="w-5 h-5 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-semibold text-foreground text-base truncate">
              {firstName} {lastName}
            </span>
            <div className="flex items-center flex-wrap gap-2 mt-0.5">
              {statusConfig && (
                <LabelBadge
                  label={statusConfig.label}
                  icon={StatusIcon}
                  variant={statusConfig.badgeVariant}
                  color={statusConfig.badgeColor}
                  size="sm"
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2 min-w-0">
            <primaryContact.icon
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--color-1-nuance-1)' }}
            />
            <span className="text-sm text-foreground truncate">{primaryContact.value}</span>
          </div>
          {primaryContact.secondaryValue && (
            <span className="text-xs text-muted-foreground pl-6">
              {primaryContact.secondaryValue}
            </span>
          )}
        </div>

        <div className="flex items-center flex-wrap gap-3">
          {PARENT_REMOTE_MILESTONES.map((m) => {
            const mStatus = getParentMilestoneStatus(record.sections, m.key);
            const Icon = m.icon;
            return (
              <div key={m.key} className="flex flex-col items-center gap-0.5" title={m.label}>
                <Icon
                  className="w-4 h-4"
                  style={{ color: mStatus === 'validated' ? m.validatedColor : m.pendingColor }}
                />
                <span
                  className="text-[10px]"
                  style={{ color: mStatus === 'validated' ? m.validatedColor : m.pendingColor }}
                >
                  {m.label}
                </span>
              </div>
            );
          })}
        </div>

        {childCount > 0 && childList.length > 0 ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 cursor-default w-fit">
                  <Users
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: 'var(--color-1-nuance-1)' }}
                  />
                  <span className="text-sm text-foreground">
                    {childCount} {childCount > 1 ? 'enfants' : 'enfant'}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="space-y-0.5">
                  {childList.map((c) => (
                    <p key={c.id}>
                      {c.firstName} {c.lastName}
                    </p>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
            <span className="text-sm text-foreground">
              {childCount} {childCount > 1 ? 'enfants' : 'enfant'}
            </span>
          </div>
        )}
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(record.id);
            }}
          />
          {pageActions?.canEditRow && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(record.userId, record);
              }}
            />
          )}
          {pageActions?.canInviteChild && (
            <Button
              preset="row-invite-child"
              onClick={(e) => {
                e.stopPropagation();
                onInviteChild?.(record.id, record);
              }}
            />
          )}
          {pageActions?.canViewStudents && (
            <Button
              preset="row-view-students"
              onClick={(e) => {
                e.stopPropagation();
                onViewStudents?.(record.id, record);
              }}
            />
          )}
          {showArchive ? <Button preset="row-archive" onClick={handleArchive} /> : null}
          {showReactivate ? <Button preset="row-reactivate" onClick={handleReactivate} /> : null}
        </div>
      </div>
    </TableCard>
  );
};
